import
  React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import
  styled
from 'styled-components';

import {
  withCommon
} from 'common/hocs';

import {
  DataStore,
  Environment,
  FontFamilies,
  Logger,
  Theme,
} from 'common';


const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background-color: ${Theme.Content.Background};
`;

const TimerDisplay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 1001;
  background-color: ${Theme.Content.Background};
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Regular};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimerText = styled.span`
  color: ${Theme.Text.Color};
  font-family: ${FontFamilies.Bold};
  font-size: 14px;
  width: 40px;
  margin-left: 5px;
`;

const EventCheckoutFrameUI = props => {
  const [expiryTime, setExpiryTime] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [showExpiredScreen, setShowExpiredScreen] = useState(false);
  const returnUrlRef = useRef(null);
  const contactRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const navigateToResult = useCallback((path) => {
    if (!returnUrlRef.current) {
      props.common.navigate(path, { replace: true });
      return;
    }

    if (path === -1) {
      window.history.back();
    } else {
      // Use replace instead of href to prevent back button navigation
      window.location.replace(`${returnUrlRef.current}${path}`);
      //window.location.href = `${returnUrlRef.current}${path}`;
    }
  }, [props.common, returnUrlRef]);

  const initCheckout = useCallback((checkoutId, entityId, order_id, token) => {
  
    try {
      if (!window.Checkout) {
        Logger.error('EventCheckoutFrame', 'initCheckout', 'Checkout object not available');
        return;
      }

      console.log('initCheckout', checkoutId, entityId, order_id, token);

      const checkout = window.Checkout.initiate({
        key: entityId,
        checkoutId: checkoutId,
        customisations: {
          paymentMethods: {
            exclude: [],
          },
          card: {
            showBillingFields: false
          }
        },
        eventHandlers: {
          onCompleted: (event) => {
            Logger.info('EventCheckoutFrame', 'onCompleted', event);
            checkout.unmount();
            navigateToResult(`/payment-result/success?order_id=${order_id || ''}&token=${encodeURIComponent(token || '')}&contact=${encodeURIComponent(contactRef.current || DataStore.get('LOGGED_IN_EMAIL') || '')}`);
          },
          onCancelled: (event) => {
            Logger.info('EventCheckoutFrame', 'onCancelled', event);
            checkout.unmount();
            navigateToResult(-1);
          },
          onError: (event) => {
            Logger.info('EventCheckoutFrame', 'onError', event);
            checkout.unmount();
            navigateToResult(`/payment-result/error?order_id=${order_id || ''}`);
          },
          onExpired: (event) => {
            Logger.info('EventCheckoutFrame', 'onExpired', event);
            checkout.unmount();
            navigateToResult(`/payment-result/expired?order_id=${order_id || ''}`);
          },
        },
      });

      checkout.render("#peach-checkout");

    } catch (error) {
      Logger.error('EventCheckoutFrame', 'initCheckout', error);
      props.common.showToast('Failed to initialize checkout. Please try again.');
    }
  }, [props.common, navigateToResult]);

  useEffect(() => {
    const searchParams = new URLSearchParams(props.common.location.search);
    const checkoutIdParam = searchParams.get('checkout_id');
    const expiryTimeParam = searchParams.get('expiry');
    const orderIdParam = searchParams.get('orderId');
    const entityIdParam = searchParams.get('entity_id');
    const tokenParam = searchParams.get('token');
    const returnUrlParam = searchParams.get('returnUrl');
    const contactParam = searchParams.get('contact');
    
    if (!checkoutIdParam) {
      Logger.error('EventCheckoutFrame', 'init', 'No checkout_id provided');
      props.common.showToast('Checkout session could not be started');
      navigateToResult(-1);
      return;
    }

    if (!expiryTimeParam) {
      Logger.error('EventCheckoutFrame', 'init', 'No checkout expiry time provided');
      navigateToResult(-1);
      return;
    }

    if (!entityIdParam) {
      Logger.error('EventCheckoutFrame', 'init', 'No entity_id provided');
      navigateToResult(-1);
      return;
    }

    if (orderIdParam) {
      setOrderId(orderIdParam);
    }

    if (returnUrlParam) {
      returnUrlRef.current = returnUrlParam;
    }

    if (contactParam) {
      contactRef.current = contactParam;
    }

    setExpiryTime(parseInt(expiryTimeParam));

    const now = Date.now();
    const secondsRemaining = Math.max(0, Math.floor((parseInt(expiryTimeParam) - now) / 1000));
    setRemainingTime(formatTime(secondsRemaining));

    // if we land on this page with less than 40 seconds remaining, we need to navigate to the expired page
    if (secondsRemaining <= 40) {
      Logger.error('EventCheckoutFrame', 'init', 'Checkout session expired');
      setShowExpiredScreen(true);
      navigateToResult(`/payment-result/expired?order_id=${orderIdParam}`);
      return;
    }

    if (!window.Checkout) {
      Logger.info('EventCheckoutFrame', 'scriptLoad', 'Peach Payments checkout script not loaded, loading now');

      const script = document.createElement('script');
      script.src = Environment.peachCheckoutUrl;
      script.async = true;
      
      script.onload = () => {

        if (window.Checkout) {
          initCheckout(checkoutIdParam, entityIdParam, orderIdParam, tokenParam);
          props.common.hideLoading();
          return;
        }

        // Fallback to attempt to load the script again after 800ms
        setTimeout(() => {
          if (window.Checkout) {
            initCheckout(checkoutIdParam, entityIdParam, orderIdParam, tokenParam);
          }
          else {
            Logger.error('EventCheckoutFrame', 'scriptLoad', 'Failed to load Peach Payments checkout on fallback');
            props.common.showToast('Failed to initialize checkout. Please refresh the page and try again.');
          }
          props.common.hideLoading();
        }, 800);
      };

      script.onerror = () => {
        Logger.error('EventCheckoutFrame', 'scriptLoad', 'Failed to load Peach Payments checkout script');
        props.common.showToast('Failed to initialize checkout. Please refresh the page and try again.');
        props.common.hideLoading();
      };

      document.head.appendChild(script);
    }
    else {
      Logger.info('EventCheckoutFrame', 'scriptLoad', 'Peach Payments checkout script already loaded');
      initCheckout(checkoutIdParam, entityIdParam, orderIdParam, tokenParam);
      props.common.hideLoading();
    }

    return () => {
      // Clean up interval on unmount
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!expiryTime) return;

    const setupExpiryCheck = () => {
      checkIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const secondsRemaining = Math.max(0, Math.floor((expiryTime - now) / 1000));

        // Update the remaining time display
        setRemainingTime(formatTime(secondsRemaining));
        
        if (secondsRemaining <= 0) {
          // Session expired
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
          }
          Logger.error('EventCheckoutFrame', 'checkExpiry', 'Checkout session expired');
          setShowExpiredScreen(true);
          navigateToResult(`/payment-result/expired?order_id=${orderId}`);
        }
      }, 1000); // Update every second
    };
    
    setupExpiryCheck();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [expiryTime, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Container>
        {remainingTime && !showExpiredScreen && (
          <TimerDisplay>
            Checkout expires in: <TimerText>{remainingTime}</TimerText>
          </TimerDisplay>
        )}
        {!showExpiredScreen && <div id="peach-checkout" style={{
          marginTop: '40px',
          height: 'calc(100vh - 40px)',
          width: '100%',
          maxWidth: '800px',
          margin: '40px auto 0 auto'
        }}></div>}
        {showExpiredScreen && <div id="expired-screen" style={{
          marginTop: '40px',
          height: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>Checkout session invalid</div>}
      </Container>
    </>
  );
};

export const EventCheckoutFrame = withCommon(EventCheckoutFrameUI, {
  showHeader: false,
  showFooter: false,
  showLoadingOnLoad: true
});

